<style scoped>
.login-method-splitter {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    text-align: center;
    @apply text-base font-black text-darkGrayishBlue my-4;
}
.login-method-splitter::before,
.login-method-splitter::after {
    content: '';
    flex: 1;
    height: 0;
    position: relative;
    top: 2px;
    @apply block my-4 border-b border-darkGrayishBlue;
}
.login-method-splitter::before {
    @apply mr-4;
}
.login-method-splitter::after {
    @apply ml-4;
}
</style>

<template>
    <div class="max-w-xl mx-auto">
        <div
            class="py-8 px-4 mt-2 sm:mt-24 sm:pt-8 sm:pb-16 sm:px-16 sm:border border-gray-100 border-dotted"
        >
            <div class="font-bold text-xl text-center mb-2 sm:mb-8">
                {{ $t('login.member.login') }}
            </div>
            <template v-if="loginWithTeamnoteUrl">
                <a
                    class="flex flex-row justify-center items-center rounded py-1 mb-2 text-white"
                    style="background: rgba(22, 90, 150, 1)"
                    :href="loginWithTeamnoteUrl"
                >
                    以
                    <div class="w-32 mx-4 pb-1">
                        <imageLoader
                            :src="teamnoteLogoImg"
                            :ratio="4"
                            :alt="'Login With TeamNote'"
                        />
                    </div>
                    登入
                </a>
                <div class="login-method-splitter">或</div>
            </template>
            <form>
                <formFieldRow
                    name="email"
                    :label="$t('login.email')"
                    labelWidthClass="sm:w-16"
                    :errors="errors"
                >
                    <template #default="{hasError}">
                        <input
                            name="email"
                            class="form-input"
                            :class="{ 'border-red': hasError }"
                            type="email"
                            v-model.trim="form.email"
                            @keydown.enter="$refs.password.focus"
                        />
                    </template>
                </formFieldRow>
                <formFieldRow
                    name="password"
                    :label="$t('login.password')"
                    labelWidthClass="sm:w-16"
                    :errors="errors"
                >
                    <template #default="{hasError}">
                        <div class="flex flex-col sm:flex-col-reverse">
                            <div class="text-sm">
                                {{ $t('login.password.hint') }}
                            </div>
                            <input
                                name="password"
                                class="form-input"
                                :class="{ 'border-red': hasError }"
                                type="password"
                                ref="password"
                                v-model.trim="form.password"
                                @keydown.enter="login"
                            />
                        </div>
                    </template>
                </formFieldRow>
            </form>
            <errorBox :message="errors.server" class="-mt-4" />
            <div class="w-full flex flex-row">
                <div class="hidden sm:block sm:w-16"></div>
                <loadingButton
                    class="py-2 px-8 text-white bg-primary border-primary flex-grow justify-center"
                    :status="status"
                    @click="login"
                >
                    {{ $t('login.login') }}
                </loadingButton>
            </div>
            <div class="flex mt-2">
                <router-link
                    tag="button"
                    to="/forgetPassword"
                    class="ml-auto text-sm py-2 hover:text-blue focus:outline-none focus:text-blue"
                >
                    <div class="border-b border-darkGrayishBlue leading-none">
                        {{ $t('login.forget.password') }}
                    </div>
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
import loadingButton from '@/components/loadingButton.vue';
import formFieldRow from '@/components/formFieldRow.vue';
import { validate } from '@/utils/validationUtil';
import errorBox from '@/components/errorBox.vue';
import { wait } from '@/utils/promiseUtil';
import img2s112 from '@/assets/img/2-112.png';
import teamnoteLogoImg from '@/assets/img/teamnote_logo.png';
import imageLoader from '@/components/imageLoader.vue';
import { instance } from '@/utils/ajax';

async function getTeamnoteClientName(cmpyToken) {
    try {
        let result = await instance.get('/api/getTeamnoteClientName', {
            params: { cmpyToken }
        });
        return result.data.clientName;
    } catch (e) {
        /*eslint-disable no-console*/
        console.error('error getting teamnote client name');
    }
    return undefined;
}

export default {
    components: {
        formFieldRow,
        loadingButton,
        errorBox,
        imageLoader
    },
    data() {
        return {
            form: {
                email: undefined,
                password: undefined
            },
            errors: {},
            status: undefined,
            teamnoteClientName: undefined
        };
    },
    created() {
        this.bubbleImg = img2s112;
        this.teamnoteLogoImg = teamnoteLogoImg;

        const cmpyToken = this.$route.params.cmpyToken;
        if (!this.$store.state.previewCmpy && cmpyToken) {
            this.$store.dispatch('getPreviewProducts', cmpyToken);
        }
    },
    async mounted() {
        const cmpyToken = this.$route.params.cmpyToken;
        if (cmpyToken) {
            this.teamnoteClientName = await getTeamnoteClientName(cmpyToken);
        }
    },
    computed: {
        loginWithTeamnoteUrl() {
            return this.teamnoteClientName
                ? `${location.origin}${process.env.BASE_URL}ssoLogin/teamnote/${this.teamnoteClientName}`
                : undefined;
        }
    },
    methods: {
        async login() {
            this.errors = {};
            const v = validate(this.errors, this.form, {
                email: { required: true },
                password: { required: true }
            });
            if (v.hasError) {
                this.errors = v.errors;
                return;
            }
            this.status = 'loading';
            let result = await wait(
                () => this.$store.dispatch('login', this.form),
                500
            );
            if (result.success) {
                this.$router.push(
                    this.$route.params.lastPath ||
                        this.$route.params.lastRoute ||
                        '/'
                );
            } else {
                this.status = undefined;
                this.errors = result.data.errors;
            }
        }
    }
};
</script>
